const ACCORDION_TRANSITION_DURATION = 300;
const ACCORDION_TRANSITION_EASING = 'ease-out';

interface AccordionItemState {
    details: HTMLDetailsElement;
    body: HTMLElement;
    animation: Animation | null;
    isClosing: boolean;
    isExpanding: boolean;
}

function prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function finish(state: AccordionItemState, open: boolean): void {
    state.details.open = open;
    state.body.style.height = '';
    state.body.style.overflow = '';
    state.animation = null;
    state.isClosing = false;
    state.isExpanding = false;
}

function collapse(state: AccordionItemState): void {
    state.isClosing = true;
    state.body.style.overflow = 'hidden';
    const startHeight = `${state.body.offsetHeight}px`;

    state.animation?.cancel();
    state.animation = state.body.animate(
        { height: [startHeight, '0px'] },
        { duration: prefersReducedMotion() ? 0 : ACCORDION_TRANSITION_DURATION, easing: ACCORDION_TRANSITION_EASING },
    );
    state.animation.onfinish = () => finish(state, false);
    state.animation.oncancel = () => {
        state.isClosing = false;
    };
}

function expand(state: AccordionItemState): void {
    const startHeight = state.isClosing ? `${state.body.offsetHeight}px` : '0px';
    state.details.open = true;
    state.isExpanding = true;
    state.body.style.overflow = 'hidden';
    const endHeight = `${state.body.scrollHeight}px`;

    state.animation?.cancel();
    state.animation = state.body.animate(
        { height: [startHeight, endHeight] },
        { duration: prefersReducedMotion() ? 0 : ACCORDION_TRANSITION_DURATION, easing: ACCORDION_TRANSITION_EASING },
    );
    state.animation.onfinish = () => finish(state, true);
    state.animation.oncancel = () => {
        state.isExpanding = false;
    };
}

function handleSummaryClick(event: MouseEvent, state: AccordionItemState): void {
    // Blocked by the backend-editing guard below - let the native (no-op) behaviour stand.
    if (event.defaultPrevented) return;
    event.preventDefault();

    if (state.details.open && !state.isExpanding) {
        collapse(state);
    } else {
        expand(state);
    }
}

export function initAccordion(): void {
    document.querySelectorAll<HTMLDetailsElement>('.accordion-item').forEach((details) => {
        const summary = details.querySelector<HTMLElement>(':scope > summary.accordion-header');
        const body = details.querySelector<HTMLElement>(':scope > .accordion-body');
        if (!summary || !body) return;

        const state: AccordionItemState = { details, body, animation: null, isClosing: false, isExpanding: false };
        summary.addEventListener('click', (event) => handleSummaryClick(event, state));
    });

    /**
     * Prevent toggling accordion when editing summary in the backend
     */
    document.addEventListener('click', (event: MouseEvent) => {
        if (!document.body.classList.contains('neos-backend')) return;

        const summary = (event.target as Element).closest('summary.accordion-header');
        if (!summary) return;

        if (!(event.target as Element).closest('.accordion-icon')) {
            event.preventDefault();
        }
    }, true);
}
